import { EStreamTradeDTO } from '../../types/estream/eStreamTrade';
import { InstrumentStoreItem, InstrumentStorePriceType } from '../../types/estream/instrumentStoreItem';
import { ListedInstrument } from '../../types/instrument/listedInstrument';
import { createDateFromEStreamStream } from '../../utils/dateUtils';
import { isDefined, mergeObjectInto, mergeSnapshotInto, when } from '../../utils/objectUtils';
import { mapDetails } from './eStreamDetailsMapper';
import { mapFundamentals } from './eStreamFundamentalsMapper';
import * as eStreamMapperConsts from './eStreamMapperConsts';
import { mapOrderBook } from './eStreamOrderBookMapper';
import { mapPrice } from './eStreamPriceMapper';

export const mapLastTradeTime = (trade: EStreamTradeDTO): Date | undefined => {
  const date = when(
    isDefined(trade.data?.TRADE_DATE) && isDefined(trade.data?.TRDTIM_1),
    createDateFromEStreamStream(trade.data?.TRADE_DATE, trade.data?.TRDTIM_1),
  )
    .or(
      isDefined(trade.data?.TRADE_DATE) && isDefined(trade.data?.TIMACT_NS),
      createDateFromEStreamStream(trade.data?.TRADE_DATE, trade.data?.TIMACT_NS),
    )
    .or(
      isDefined(trade.data?.TRADE_DATE) && isDefined(trade.data?.SALTIM_MS),
      createDateFromEStreamStream(
        trade.data?.TRADE_DATE,
        ((): string => {
          const time = trade.data!.SALTIM_MS as number;
          const ms = time % 1000;
          const sec = Math.floor(time / 1000) % 60;
          const min = Math.floor(time / 60000) % 60;
          const hours = Math.floor(time / 3600000) % 24;
          return `${hours}:${min}:${sec}.${ms}`;
        })(),
      ),
    )
    .otherwise(new Date())
    .get();

  return date;
};

export const mapEStreamTradeToInstrumentStoreItem = (
  item: EStreamTradeDTO,
  previousValue: InstrumentStoreItem,
  listedInstrument: ListedInstrument | undefined,
  hasRealtimeAccessToMarket: boolean,
  formatDate: (date: Date) => string,
  nonTradingDays: Record<string, Array<string>>,
  isSnapshot = false,
): Partial<InstrumentStoreItem> => {
  const ric = item.ric;
  const mic = listedInstrument?.mic;

  const priceSkipped = isSnapshot && !!previousValue.price;
  const price = priceSkipped
    ? previousValue.price
    : mapPrice({
        item,
        previousValue,
        hasRealtimeAccess: hasRealtimeAccessToMarket,
        isPolled: item.isPolled === true,
        listedInstrument,
        formatDate,
        nonTradingDays,
        consts: eStreamMapperConsts,
      });
  const details = mergeObjectInto(previousValue.details, mapDetails(listedInstrument, item, ric), []);
  const fundamentals = mergeObjectInto(previousValue.fundamentals, mapFundamentals(item), []);
  const raw = mergeObjectInto(previousValue.raw || {}, item.data || {}, ['OFF_CLOSE', 'OFF_CLS_DT']);
  const bookSkipped = isSnapshot && !!previousValue.book?.asks?.some(([p, v]) => p !== 0 || v !== 0);
  const book = bookSkipped ? previousValue.book : mapOrderBook(item, previousValue);
  const lastTradeTime = mapLastTradeTime(item);

  const currency =
    price?.type === InstrumentStorePriceType.FromInstrumentService
      ? listedInstrument?.currency
      : item.data?.CURRENCY ?? listedInstrument?.currency;

  const res: Partial<InstrumentStoreItem> = {
    mic,
    ccy: currency,
    closePrice: item?.data?.HST_CLOSE,
    lastTradeTime,
    openPrice: item?.data?.OPEN_PRC,
    lastTradeQty: item?.data?.TRDVOL_1,
    ric: item.data?.ric,
    bestAsk:
      item?.data?.ASK === 'M'
        ? { isMarketPrice: true }
        : {
            isMarketPrice: false,
            value: item?.data?.ASK || (previousValue.bestAsk as { value?: number })?.value,
          },
    bestBid:
      item?.data?.BID === 'M'
        ? { isMarketPrice: true }
        : {
            isMarketPrice: false,
            value: item?.data?.BID || (previousValue.bestBid as { value?: number })?.value,
          },
    exchangeId: item?.data?.RDN_EXCHID,
    book,
    price,
    details,
    fundamentals,
    raw,
  };

  // DEBUG: Snapshot merge logging - TEMPORARY
  if (isSnapshot) {
    const merged = mergeSnapshotInto(previousValue, res);
    const preserved: string[] = [];
    const filled: string[] = [];

    const logSubObject = (
      prefix: string,
      prev: Record<string, any> | undefined,
      incoming: Record<string, any> | undefined,
    ) => {
      if (!incoming) return;
      const prevObj = prev ?? {};
      for (const subKey of Object.keys(incoming)) {
        const prevVal = prevObj[subKey];
        const newVal = incoming[subKey];
        const hasPrev = prevVal !== undefined && prevVal !== null;
        const hasNew = newVal !== undefined && newVal !== null;
        if (hasPrev && hasNew) {
          preserved.push(`${prefix}.${subKey} (kept: ${JSON.stringify(prevVal)}, ignored: ${JSON.stringify(newVal)})`);
        } else if (!hasPrev && hasNew) {
          filled.push(`${prefix}.${subKey} (set to: ${JSON.stringify(newVal)})`);
        }
      }
    };

    const resKeys = Object.keys(res) as Array<keyof InstrumentStoreItem>;
    for (const key of resKeys) {
      if (key === 'raw') continue;
      // details and fundamentals: per-key logging
      if (key === 'details' || key === 'fundamentals') {
        logSubObject(
          key,
          previousValue[key] as Record<string, any> | undefined,
          res[key] as Record<string, any> | undefined,
        );
        continue;
      }
      const prevVal = previousValue[key];
      const newVal = res[key];
      const hasPrev = prevVal !== undefined && prevVal !== null;
      const hasNew = newVal !== undefined && newVal !== null;
      if (hasPrev && hasNew) {
        preserved.push(`${key} (kept: ${JSON.stringify(prevVal)}, ignored: ${JSON.stringify(newVal)})`);
      } else if (!hasPrev && hasNew) {
        filled.push(`${key} (set to: ${JSON.stringify(newVal)})`);
      }
    }
    if (priceSkipped) {
      preserved.push(`price (kept existing, skipped state machine)`);
    }
    if (bookSkipped) {
      preserved.push(`book (kept existing order book)`);
    }

    return merged;
  }

  return mergeObjectInto(previousValue, res, []);
};
